/**
 * src/shell/portal/components/SponsorVaultChannelView.tsx
 *
 * Dedicated Opt-in #sponsor-vault Channel Experience.
 * Users review interactive sponsor spotlights, solve the Human Pulse kinetic challenge,
 * and claim 60% direct attention dividends with 10-minute rate-limiting protection.
 */

import React, { useState, useEffect } from 'react';
import { adEscrowLedger, AdCampaign } from '../../../domain/attention/ad-escrow-ledger';
import { HumanPulseCanvas } from './HumanPulseCanvas';
import { ClassificationReport } from '../../../domain/attention/human-pulse-classifier';
import { Sparkles, Play, CheckCircle2, Clock, ShieldCheck, X, Award } from 'lucide-react';

export interface SponsorVaultChannelViewProps {
  currentUserDid?: string;
  onDividendClaimed?: (tokens: number) => void;
}

export const SponsorVaultChannelView: React.FC<SponsorVaultChannelViewProps> = ({
  currentUserDid = 'did:key:z6MkpioneerUser',
  onDividendClaimed,
}) => {
  const [campaigns, setCampaigns] = useState<AdCampaign[]>(() => adEscrowLedger.getAllActiveCampaigns());
  const [selectedCampaign, setSelectedCampaign] = useState<AdCampaign | null>(null);
  const [isPulseModalOpen, setIsPulseModalOpen] = useState<boolean>(false);
  const [cooldownRemainingMs, setCooldownRemainingMs] = useState<number>(0);
  const [lastClaimMessage, setLastClaimMessage] = useState<string | null>(null);

  useEffect(() => {
    const updateCooldown = () => {
      const remaining = adEscrowLedger.getRemainingCooldownMs(currentUserDid);
      setCooldownRemainingMs(remaining);
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [currentUserDid]);

  const handleStartClaim = (campaign: AdCampaign) => {
    const remaining = adEscrowLedger.getRemainingCooldownMs(currentUserDid);
    if (remaining > 0) {
      const min = Math.ceil(remaining / 60000);
      alert(`Rate-limit active: Please wait ${min}m before claiming again.`);
      return;
    }
    setSelectedCampaign(campaign);
    setIsPulseModalOpen(true);
  };

  const handlePulseVerified = (report: ClassificationReport) => {
    if (!selectedCampaign) return;

    try {
      const settlement = adEscrowLedger.settleImpression({
        campaignId: selectedCampaign.id,
        channelId: 'chan_sponsor_vault',
        creatorDid: 'did:key:channelOwner',
        viewerDid: currentUserDid,
        isVaultDirectClaim: true,
      });

      setIsPulseModalOpen(false);
      setCampaigns(adEscrowLedger.getAllActiveCampaigns());
      setLastClaimMessage(`Successfully claimed +${settlement.distribution.viewerTokens} Tokens!`);

      if (onDividendClaimed) {
        onDividendClaimed(settlement.distribution.viewerTokens);
      }
    } catch (err: any) {
      alert(err.message || 'Failed to settle attention dividend.');
    }
  };

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1000, margin: '0 auto', color: '#f8fafc' }}>
      {/* Vault Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.15) 0%, rgba(15, 23, 42, 0.6) 100%)',
          border: '1px solid #1e293b',
          borderRadius: 16,
          padding: '24px 28px',
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'rgba(56, 189, 248, 0.15)',
                border: '1px solid #38bdf8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Award size={22} color="#38bdf8" />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 900, letterSpacing: -0.5 }}>
                #sponsor-vault Attention Dividend Hub
              </h1>
              <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 3 }}>
                Earn 60% of sponsor campaign budgets by solving Human Pulse kinetic gestures.
              </div>
            </div>
          </div>
        </div>

        {/* Rate limit status card */}
        <div
          style={{
            background: '#070a10',
            border: '1px solid #1e293b',
            borderRadius: 10,
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12,
          }}
        >
          <Clock size={16} color={cooldownRemainingMs > 0 ? '#f59e0b' : '#34d399'} />
          <div>
            <div style={{ color: '#94a3b8', fontSize: 11 }}>Anti-Farming Rate Limit</div>
            <div style={{ fontWeight: 700, color: cooldownRemainingMs > 0 ? '#fbbf24' : '#34d399' }}>
              {cooldownRemainingMs > 0
                ? `Cooldown: ${Math.ceil(cooldownRemainingMs / 1000)}s remaining`
                : 'Ready to Claim (1 / 10m)'}
            </div>
          </div>
        </div>
      </div>

      {lastClaimMessage && (
        <div
          style={{
            background: 'rgba(16, 185, 129, 0.15)',
            border: '1px solid #10b981',
            borderRadius: 10,
            padding: '12px 18px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: '#34d399',
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          <CheckCircle2 size={16} />
          {lastClaimMessage}
        </div>
      )}

      {/* Campaigns Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {campaigns.map((camp) => {
          const viewerPayout = Math.round(camp.costPerImpressionTokens * 0.6 * 100) / 100;
          const isCoolingDown = cooldownRemainingMs > 0;

          return (
            <div
              key={camp.id}
              style={{
                background: '#0c101a',
                border: '1px solid #1e293b',
                borderRadius: 14,
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 14,
              }}
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#0284c7' }}>
                    {camp.category}
                  </span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>
                    {camp.remainingBudgetTokens} tokens left
                  </span>
                </div>

                <div style={{ fontWeight: 800, fontSize: 16, color: '#f8fafc', lineHeight: 1.3 }}>
                  {camp.headline}
                </div>
                <div style={{ fontSize: 11, color: '#38bdf8', fontWeight: 600, marginTop: 4 }}>
                  by {camp.sponsorName}
                </div>

                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8, lineHeight: 1.4 }}>
                  {camp.tagline}
                </div>
              </div>

              <div style={{ borderTop: '1px solid #1e293b', paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 10, color: '#64748b' }}>Your Attention Dividend</div>
                  <div style={{ fontWeight: 900, fontSize: 15, color: '#34d399' }}>
                    +{viewerPayout} Tokens
                  </div>
                </div>

                <button
                  type="button"
                  disabled={isCoolingDown}
                  onClick={() => handleStartClaim(camp)}
                  style={{
                    background: isCoolingDown
                      ? 'rgba(51, 65, 85, 0.4)'
                      : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 14px',
                    color: isCoolingDown ? '#64748b' : '#ffffff',
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: isCoolingDown ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Play size={12} fill={isCoolingDown ? '#64748b' : '#ffffff'} />
                  {isCoolingDown ? 'In Cooldown' : 'Solve Pulse'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Human Pulse Modal */}
      {isPulseModalOpen && selectedCampaign && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsPulseModalOpen(false);
          }}
        >
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setIsPulseModalOpen(false)}
              style={{
                position: 'absolute',
                top: 14,
                right: 14,
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '50%',
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#94a3b8',
                cursor: 'pointer',
                zIndex: 10,
              }}
            >
              <X size={16} />
            </button>
            <HumanPulseCanvas
              rewardAmountTokens={Math.round(selectedCampaign.costPerImpressionTokens * 0.6 * 100) / 100}
              onVerified={handlePulseVerified}
              onCancel={() => setIsPulseModalOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
